import React, { useState } from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import { Chip } from '@material-ui/core'
import { Add as AddIcon } from '@material-ui/icons'

import { useQueryPaginator } from 'src/components/Table'
import { TruckloadDetailTable } from 'src/components/Table/detail/Truckload'
import { TruckloadDialog } from 'src/modules/supply_chain/components/freight/TruckloadDialog'
import { FreightShipmentListTable } from 'src/components/Table/list/FreightShipment'
import { FreightShipmentUnitListTable } from 'src/components/Table/list/FreightShipmentUnit'
import { FreightShipmentBarcodedUnitListTable } from 'src/components/Table/list/FreightShipmentBarcodedUnit'
import { FreightShipmentPalletListTable } from 'src/components/Table/list/FreightShipmentPallet'
import { EventListTable } from 'src/components/Table/list/Event'
import { GenericPreview, PaginatedFilePreviewGallery } from 'src/components/FilePreview'
import { Tag } from 'src/api/models'
import { AddAttachmentsDialog } from 'src/components/Dialog/AddAttachmentsDialog'

import { skuit } from 'src/services/skuit'
import { useQuery } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'
import { DetailTab } from 'src/components/Pages/Tab'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathProps {
  guid: string
}

const DefaultTab = ({ guid }: { guid: string }) => {
  const shipmentPaginator = useQueryPaginator(
    'truckload-shipment-list',
    skuit.sc.freight.shipments.list,
    { params: { page_size: 5, truckloads: guid } }
  )

  return (
    <DetailTab>
      <FreightShipmentListTable
        paginator={shipmentPaginator}
        title="Shipments"
        fields={['trackingNumber', 'order', 'consignee', 'status']}
      />
    </DetailTab>
  )
}

const InventoryTab = ({ guid }: { guid: string }) => {
  const unitsPaginator = useQueryPaginator(
    'truckload-unit-list',
    skuit.sc.freight.shipments.units.list,
    { params: { page_size: 5, truckloads: guid } }
  )
  const barcodedPaginator = useQueryPaginator(
    'truckload-barcoded-unit-list',
    skuit.sc.freight.shipments.barcodedUnits.list,
    { params: { page_size: 5, truckloads: guid } }
  )
  const palletsPaginator = useQueryPaginator(
    'truckload-pallet-list',
    skuit.sc.freight.shipments.pallets.list,
    { params: { page_size: 5, truckloads: guid } }
  )

  return (
    <DetailTab>
      <FreightShipmentUnitListTable
        paginator={unitsPaginator}
        title="Units"
        fields={['productName', 'product', 'sku', 'numUnits', 'order', 'shipment', 'container']}
      />
      <FreightShipmentBarcodedUnitListTable
        paginator={barcodedPaginator}
        title="Units (Barcoded)"
        fields={['unit', 'product', 'shipment']}
      />
      <FreightShipmentPalletListTable
        paginator={palletsPaginator}
        title="Pallets"
        fields={['pallet', 'clientID', 'skus', 'path', 'shipment']}
      />
    </DetailTab>
  )
}

const EventsTab = ({ guid }: { guid: string }) => {
  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  return (
    <DetailTab>
      <EventListTable
        paginator={eventsPaginator}
        title="Truckload Events"
        fields={['type', 'data', 'by', 'on']}
      />
    </DetailTab>
  )
}

const AttachmentsTab = ({ guid }: { guid: string }) => {
  const [addAttachmentDialogOpen, setAddAttachmentDialogOpen] = useState(false)

  const attachmentsPaginator = useQueryPaginator(
    'truckload-attachments',
    () => skuit.sc.freight.truckloads.attachments.list(guid),
    {
      params: { page_size: 4, orders: guid },
    }
  )

  return (
    <DetailTab>
      {addAttachmentDialogOpen && (
        <AddAttachmentsDialog
          onSuccess={attachmentsPaginator.refresh}
          addAttachment={(config) =>
            skuit.sc.freight.truckloads.attachments.create(guid, config?.data, config)
          }
          open={addAttachmentDialogOpen}
          handleClose={() => {
            setAddAttachmentDialogOpen(false)
          }}
        />
      )}
      <PaginatedFilePreviewGallery
        title="Attachments"
        paginator={attachmentsPaginator}
        getIdent={(item) => item.guid}
        onAdd={() => {
          setAddAttachmentDialogOpen(true)
        }}
        renderItem={(item) => {
          return (
            <>
              <GenericPreview url={item.file} />
              <div>
                {item.tags.map((tag: Tag) => (
                  <Chip key={tag.slug} label={tag.name} />
                ))}
              </div>
            </>
          )
        }}
      />
    </ DetailTab>
  )
}

export const TruckloadPage = () => {
  const { guid } = useParams<PathProps>()
  const [createOpen, setCreateOpen] = useState(false)

  const truckload = useQuery(['truckload-details', guid], () =>
    skuit.sc.freight.truckloads.retrieve(guid)
  )
  const customFieldValuePaginator = useQueryPaginator(
    'custom-field-values',
    skuit.sc.asn.customFieldValues.list,
    { params: { asn: guid } }
  )
  const customFieldValues: Record<string, string> = {}
  for (const field of customFieldValuePaginator.data.results) {
    customFieldValues[field.name] = field.value
  }

  const refresh = () => {
    truckload.refetch()
  }

  return (
    <>
      {createOpen && (
        <TruckloadDialog
          open={createOpen}
          handleClose={() => {
            setCreateOpen(false)
          }}
        />
      )}
      <DetailPage
        obj={truckload}
        title="Truckload"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'Truckloads', url: '/freight/truckloads' },
        ]}
        detailComponent={
          <TruckloadDetailTable
            obj={truckload}
            refresh={refresh}
            customFields={customFieldValues}
          />
        }
        menuItems={[
          {
            Icon: AddIcon,
            actions: [{ title: 'New Truckload', onClick: () => setCreateOpen(true) }],
          },
        ]}
        tabs={['Details', 'Inventory', 'Attachments', 'Events']}
      >
        <DefaultTab guid={guid} />
        <InventoryTab guid={guid} />
        <AttachmentsTab guid={guid} />
        <EventsTab guid={guid} />
      </DetailPage>
    </>
  )
}
